import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import IconCreditCard from "@/assets/icons/credit-card.svg?react";
import IconClose from "@/assets/icons/close.svg?react";
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer.tsx";
import VibratingButton from "@/components/base/button/VibratingButton.tsx";
import { formatCardExpiry, formatCardLabel, useMyCards } from "../api.ts";
import { useCardsStore } from "../store.ts";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

// P2P review sahifasi uchun karta almashtirgich. Faqat Zustand store'dagi
// selectedCardId ni yangilaydi — backend default'iga tegmaydi, shuning
// uchun P2P uchun boshqa karta tanlash global default'ni o'zgartirmaydi.
export default function CardSwitcherSheet({ isOpen, onClose }: Props) {
  const { t } = useTranslation();
  const { data: cards = [] } = useMyCards();
  const selectedCardId = useCardsStore((s) => s.selectedCardId);
  const setSelectedCardId = useCardsStore((s) => s.setSelectedCardId);

  // Tanlangan kartaning ID'sini activeCard mantig'iga (Page.tsx) muvofiq
  // hisoblaymiz: store'dagi tanlov bo'lsa o'sha, aks holda backend default,
  // aks holda birinchi karta. Aks holda store bo'sh bo'lganda hech qaysi
  // qator "tanlangan" ko'rinmasdi.
  const activeId =
    (selectedCardId && cards.find((c) => c.id === selectedCardId)?.id) ||
    cards.find((c) => c.is_default)?.id ||
    cards[0]?.id;

  const handleSelect = (id: string) => {
    setSelectedCardId(id);
    onClose();
  };

  return (
    <Drawer open={isOpen} onOpenChange={(e) => !e && onClose()}>
      <DrawerContent className="bg-gray-4 max-h-[85vh]" showTopThumb>
        <DrawerHeader className="flex justify-between items-center">
          <DrawerTitle className="text-lg">
            {t("p2p_select_card") || "Karta tanlang"}
          </DrawerTitle>
          <DrawerClose>
            <IconClose className="size-6 text-gray-1 cursor-pointer" />
          </DrawerClose>
        </DrawerHeader>

        <DrawerBody className="flex-1 overflow-y-auto px-4 pb-2">
          <div className="flex flex-col gap-2">
            {cards.map((card) => {
              const isActive = card.id === activeId;
              const expiry = formatCardExpiry(card);
              return (
                <VibratingButton
                  key={card.id}
                  onClick={() => handleSelect(card.id)}
                  vibrationType="selection"
                  className={`w-full text-left rounded-xl p-3 flex items-center gap-3 border transition-colors bg-white/[0.04] ${
                    isActive ? "border-orange-100/40" : "border-white/08"
                  }`}
                  aria-pressed={isActive}
                >
                  <div className="size-11 rounded-lg bg-orange-100/15 flex-center shrink-0">
                    <IconCreditCard className="size-6 text-orange-100" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white leading-130 truncate">
                      {formatCardLabel(card)}
                    </p>
                    <p className="text-xs text-gray-1 leading-130 mt-0.5 truncate">
                      {card.owner ? `${card.owner}` : ""}
                      {card.owner && expiry ? " · " : ""}
                      {expiry}
                    </p>
                  </div>
                  {isActive && (
                    <div className="size-6 rounded-full bg-orange-100 flex-center shrink-0">
                      <Check className="size-4 text-dark" strokeWidth={3} />
                    </div>
                  )}
                </VibratingButton>
              );
            })}
          </div>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
